/**
 * A visit's certification period (`visits.certification_period_start/_end`),
 * and the "based on the visit date" option.
 *
 * Agencies that don't have the period yet (or whose period simply starts on
 * the visit) tick "Based on the visit date" instead of typing two dates. The
 * visit then carries `certification_period_from_visit_date = true` and the
 * dates follow the visit: blank until a nurse is scheduled, set from the
 * scheduled day when one is, moved when the visit is rescheduled, and set from
 * the day the nurse actually clocks in. From post-processing on they are frozen, so
 * the finished documents and the HHAX push never see them move.
 *
 * Same length rule as the portal's manual autofill: end = start + 180 days.
 */
export const CERTIFICATION_PERIOD_DAYS = 180;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Adds days to a calendar date (YYYY-MM-DD). Null when the input is not a real date. */
export function addDaysToYmd(ymd, days) {
    const m = YMD_RE.exec(ymd);
    if (!m) {
        return null;
    }
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    // Date.UTC rolls 2026-02-31 over into March; refuse it instead.
    if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== ymd) {
        return null;
    }
    d.setUTCDate(d.getUTCDate() + days);
    return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}
/**
 * The certification period for a visit on `visitDate` (YYYY-MM-DD): it starts
 * that day and ends CERTIFICATION_PERIOD_DAYS later. Null when there is no
 * visit date yet (or it is not a real date).
 */
export function certificationPeriodFromVisitDate(visitDate) {
    if (!visitDate) {
        return null;
    }
    const start = visitDate.slice(0, 10);
    const end = addDaysToYmd(start, CERTIFICATION_PERIOD_DAYS);
    return end ? { start, end } : null;
}
/**
 * The CSV "Certification Start" / "Certification End" cell that asks for the
 * period to follow the visit date instead of carrying a date: "Visit Date",
 * "Based on visit date", "Based off the visit date" (any case and spacing).
 */
export function isVisitDateCertificationCell(raw) {
    if (!raw) {
        return false;
    }
    const t = raw.trim().toLowerCase().replace(/\s+/g, " ");
    return /^(based (on|off)( of)? (the )?)?visit date$/.test(t);
}
//# sourceMappingURL=certification-period.js.map